import React from 'react';
import {
  Button,
  InputAdornment,
  MenuItem,
  TextField,
} from '@material-ui/core';
import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import esLocale from 'date-fns/locale/es';
import SearchIcon from '@material-ui/icons/Search';
import FilterListIcon from '@material-ui/icons/FilterList';
import { PRIMARY, PRIMARY_80, WHITE } from '@/theme/theme';
import {
  classifyRangeDay,
  EMPTY_FILTERS,
  MIN_SEARCH_TERM_LENGTH,
  SEARCH_KEY_LABELS,
  SEARCH_MIN_LENGTH_HINT,
  STATUS_FILTER_ALL_LABEL,
  STATUS_LABELS,
} from '../constants';
import type {
  AssistedAffiliationFilterBarProps,
  AssistedAffiliationStatus,
  SearchKey,
} from '../types';
import useStyle from '../styles';

// La app no monta un ThemeProvider de Material-UI, así que el popup del calendario sale con el
// azul por defecto de la librería. Este tema alcanza solo a la barra de filtros.
const filterBarTheme = createTheme({
  palette: {
    primary: { main: PRIMARY, dark: PRIMARY_80, contrastText: WHITE },
  },
});

// Sin estado propio: la consulta es de la pantalla, así que aquí solo se reportan los cambios.
// El rango son dos `KeyboardDatePicker` porque la app no tiene un date range.
const AssistedAffiliationFilterBar = ({
  filters,
  onFiltersChange,
}: AssistedAffiliationFilterBarProps) => {
  const classes = useStyle();
  const { searchKey, searchTerm, dateFrom, dateTo, status } = filters;

  const updateFilter = <K extends keyof typeof filters>(
    key: K,
    value: typeof filters[K],
  ) => onFiltersChange({ ...filters, [key]: value });

  // Al cambiar el criterio, el texto escrito ya no aplica sobre el campo nuevo: se limpia en el
  // mismo cambio para no consultar por un dato que el usuario escribió pensando en otra columna.
  const changeSearchKey = (nextSearchKey: SearchKey) =>
    onFiltersChange({ ...filters, searchKey: nextSearchKey, searchTerm: '' });

  // El día se envuelve en vez de reemplazarse para conservar intactos el foco y el clic del picker.
  const renderRangeDay = (
    day: Date | null,
    _selected: Date | null,
    dayInCurrentMonth: boolean,
    dayComponent: React.ReactElement,
  ) => {
    const kind = day && dayInCurrentMonth ? classifyRangeDay(day, dateFrom, dateTo) : null;
    if (!kind) return dayComponent;
    const bandClass = kind === 'endpoint' ? classes.rangeEndpointDay : classes.rangeBandDay;
    return <div className={bandClass}>{dayComponent}</div>;
  };

  return (
    <ThemeProvider theme={filterBarTheme}>
      <MuiPickersUtilsProvider utils={DateFnsUtils} locale={esLocale}>
      <div className={classes.filterBar}>
        {/* El `id` ata la etiqueta al campo: sin él, el control no tiene nombre accesible y quien
            usa lector de pantalla solo oye el valor elegido. */}
        <TextField
          select
          id="assisted-affiliation-search-key"
          label="Buscar por"
          value={searchKey}
          onChange={(event) => changeSearchKey(event.target.value as SearchKey)}
          className={classes.filterField}
          variant="outlined"
          size="small"
        >
          {Object.entries(SEARCH_KEY_LABELS).map(([key, label]) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
        </TextField>

        {/* Sin `displayEmpty` Material-UI no dibuja el texto de la opción cuyo valor es vacío: el
            campo se queda en blanco al elegir "Todos", como si la selección no hubiera entrado.
            Con la etiqueta fija arriba el campo se ve igual que "Buscar por", que siempre tiene valor. */}
        <TextField
          select
          id="assisted-affiliation-status"
          label="Estado"
          value={status ?? ''}
          onChange={(event) => updateFilter(
            'status',
            (event.target.value || undefined) as AssistedAffiliationStatus | undefined,
          )}
          className={classes.filterField}
          variant="outlined"
          size="small"
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
          InputProps={{ notched: true }}
        >
          <MenuItem value="">{STATUS_FILTER_ALL_LABEL}</MenuItem>
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
        </TextField>

        {/* El aviso aparece solo mientras lo escrito no alcanza para consultar, que es justo cuando
            el usuario no entiende por qué no pasa nada. Se mira el texto sin recortar para que
            escribir solo espacios también lo muestre. */}
        <TextField
          placeholder="Buscar..."
          value={searchTerm}
          onChange={(event) => updateFilter('searchTerm', event.target.value)}
          className={classes.searchField}
          variant="outlined"
          size="small"
          helperText={
            searchTerm.length > 0 && searchTerm.trim().length < MIN_SEARCH_TERM_LENGTH
              ? SEARCH_MIN_LENGTH_HINT
              : ''
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/* El rango viaja junto: si la barra tiene que bajar algo, baja las dos fechas, no una. */}
        <div className={classes.dateRange}>
        <KeyboardDatePicker
          className={classes.dateField}
          id="assisted-affiliation-date-from"
          label="Desde"
          value={dateFrom}
          onChange={(date) => updateFilter('dateFrom', date)}
          renderDay={renderRangeDay}
          maxDate={dateTo || undefined}
          format="dd/MM/yyyy"
          placeholder="dd/mm/aaaa"
          variant="inline"
          inputVariant="outlined"
          size="small"
          autoOk
          disableFuture
          invalidDateMessage="Fecha inválida"
          maxDateMessage={dateTo ? 'No puede ser posterior a la fecha final' : 'No puede ser una fecha futura'}
          KeyboardButtonProps={{ 'aria-label': 'fecha-desde' }}
        />

        <KeyboardDatePicker
          className={classes.dateField}
          id="assisted-affiliation-date-to"
          label="Hasta"
          value={dateTo}
          onChange={(date) => updateFilter('dateTo', date)}
          renderDay={renderRangeDay}
          minDate={dateFrom || undefined}
          format="dd/MM/yyyy"
          placeholder="dd/mm/aaaa"
          variant="inline"
          inputVariant="outlined"
          size="small"
          autoOk
          disableFuture
          invalidDateMessage="Fecha inválida"
          minDateMessage="No puede ser anterior a la fecha inicial"
          maxDateMessage="No puede ser una fecha futura"
          KeyboardButtonProps={{ 'aria-label': 'fecha-hasta' }}
        />
        </div>

        <Button
          className={classes.clearFilters}
          onClick={() => onFiltersChange(EMPTY_FILTERS)}
          startIcon={<FilterListIcon />}
        >
          Limpiar filtros
        </Button>
      </div>
      </MuiPickersUtilsProvider>
    </ThemeProvider>
  );
};

export default AssistedAffiliationFilterBar;
